import { useState } from 'react';
import { useParams } from 'react-router-dom';
import type { MonthlyReview, ReviewLine, StoreResponse } from '@clarity/contract';
import { useAmBrief, useMonthlyReview } from '../../queries/index.js';
import { useScopeContextOptional } from '../../lib/scopeContext.js';
import { useI18n } from '../../lib/i18n.js';
import { errorStatus, num } from '../format.js';

/**
 * The owner MONTHLY REVIEW page at
 * `/c/:clientId/store/:storeId/monthly-review/:month` (clarity-monthly-campaign-
 * review Req 7) — the read-only surface the WhatsApp template's URL button opens.
 * It reproduces the WhatsApp review content: per-campaign verdict, expectation,
 * same-campaign history, similar-by-terms, decision + deadline, plus the AM brief
 * with a copy action. Built to `brand/monthly-review-whatsapp-mock.html`.
 *
 * The review is RECOMPUTED server-side from persisted data on every read (never a
 * stored snapshot), so an accuracy upgrade or a catalog change is reflected. The
 * thread shows the top-3 campaigns by JOD impact inline, the rest behind a
 * "See all" tap (the generator returns all lines ordered by impact; the slice is
 * a UI choice). Bilingual + RTL (logical props, `--font-num` tabular figures),
 * WCAG 2.1 AA.
 */
export function MonthlyReviewPage({ store: storeProp }: { store?: StoreResponse }) {
  const { storeId, month } = useParams();
  const { t, locale } = useI18n();

  const scope = useScopeContextOptional();
  const store = storeProp ?? scope?.store ?? null;
  const effectiveStoreId = store?.id ?? storeId ?? '';
  const effectiveMonth = month ?? '';

  const review = useMonthlyReview(effectiveStoreId, effectiveMonth, Boolean(effectiveStoreId && effectiveMonth));
  const brief = useAmBrief(effectiveStoreId, effectiveMonth, locale, Boolean(effectiveStoreId && effectiveMonth));

  const body: MonthlyReview | null = review.data?.status === 200 ? review.data.body : null;
  const notPublished = review.isError && errorStatus(review.error) === 404;

  if (review.isLoading) {
    return (
      <p role="status" className="muted">
        {t('review.loading')}
      </p>
    );
  }
  if (notPublished) {
    return (
      <p role="status" className="muted monthly-review__empty">
        {t('review.notPublished')}
      </p>
    );
  }
  if (review.isError || body === null) {
    return (
      <p role="alert" className="error">
        {t('review.error.title')}
      </p>
    );
  }

  return (
    <section className="monthly-review" aria-label={t('review.title')}>
      <div className="page-head">
        <div>
          <h2>{t('review.title')}</h2>
          <p className="sub">
            {body.storeLabel} · {t('review.month', { month: body.month })}
          </p>
        </div>
      </div>

      {body.coFundNote && (
        <p className="monthly-review__cofund muted" role="note">
          {t('review.coFundNote')}
        </p>
      )}

      <ReviewLines lines={body.lines} />

      <AmBriefCard
        text={brief.data?.status === 200 ? brief.data.body.text : ''}
        loading={brief.isLoading}
      />
    </section>
  );
}

/** The top-3 inline + "See all" thread of campaign lines (Req 7, depth decision). */
function ReviewLines({ lines }: { lines: ReviewLine[] }) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const TOP = 3;
  const shown = expanded ? lines : lines.slice(0, TOP);
  const rest = lines.length - TOP;

  return (
    <div className="monthly-review__lines">
      <ol className="monthly-review__list">
        {shown.map((line) => (
          <li key={line.campaignId}>
            <ReviewLineCard line={line} />
          </li>
        ))}
      </ol>
      {rest > 0 && (
        <button
          type="button"
          className="ghost monthly-review__see-all"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? t('review.seeLess') : t('review.seeAll', { count: num(rest) })}
        </button>
      )}
    </div>
  );
}

/** The translate fn shape, narrowed to what the helpers need. */
type T = (key: Parameters<ReturnType<typeof useI18n>['t']>[0], vars?: Record<string, string | number>) => string;

/** One campaign's card: verdict badge, description, expectation, receipts, decision. */
function ReviewLineCard({ line }: { line: ReviewLine }) {
  const { t, locale } = useI18n();
  const name = locale === 'ar' && line.arabicName ? line.arabicName : line.name;

  return (
    <div className={`card monthly-review__line monthly-review__line--${line.verdict}`}>
      <div className="monthly-review__line-head">
        <span className="monthly-review__name">{name}</span>
        <span className={`monthly-review__verdict monthly-review__verdict--${line.verdict}`}>
          {t(`review.verdict.${line.verdict}` as Parameters<T>[0])}
          {line.marginal && <span className="monthly-review__marginal"> · {t('review.marginal')}</span>}
        </span>
      </div>

      <p className="monthly-review__desc muted">{line.description}</p>

      {line.expectedProfitPerOrder !== null && (
        <p className="monthly-review__expect num">
          {t('review.expected', { profit: num(line.expectedProfitPerOrder) })}
          {line.expectedOrders && (
            <> · {t('review.expectedOrders', { low: num(line.expectedOrders.low), high: num(line.expectedOrders.high) })}</>
          )}
        </p>
      )}

      {line.history && (
        <p className="monthly-review__history num">
          {t('review.history', {
            month: line.history.monthLabel,
            orders: num(line.history.orders),
            disc: num(line.history.fundedDiscount),
          })}
          {line.history.agrees && (
            <span className="monthly-review__agrees"> · {t('review.history.agrees')}</span>
          )}
        </p>
      )}

      {!line.history && line.similar && (
        <p className="monthly-review__similar num">
          {t('review.similar', {
            label: line.similar.label,
            profit: num(line.similar.avgProfitPerOrder),
            orders: num(line.similar.orderCount),
          })}
        </p>
      )}

      {line.includeItems.length > 0 && (
        <p className="monthly-review__items monthly-review__items--in">
          {t('review.include', { items: line.includeItems.join(', ') })}
        </p>
      )}
      {line.excludeItems.length > 0 && (
        <p className="monthly-review__items monthly-review__items--out">
          {t('review.exclude', { items: line.excludeItems.join(', ') })}
        </p>
      )}

      <p className="monthly-review__decision">{line.decision}</p>

      {line.deadline && (
        <p className={`monthly-review__deadline${line.deadlineTooLate ? ' monthly-review__deadline--late' : ''} num`}>
          {t('review.deadline', { date: line.deadline })}
          {line.deadlineTooLate && <> · {t('review.deadline.late')}</>}
        </p>
      )}
    </div>
  );
}

/** The AM brief card with a copy-to-clipboard action. */
function AmBriefCard({ text, loading }: { text: string; loading: boolean }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be unavailable (insecure context / test env); the textarea
      // is still selectable as a fallback.
      setCopied(false);
    }
  }

  return (
    <div className="card monthly-review__brief">
      <div className="monthly-review__brief-head">
        <h3>{t('review.brief.head')}</h3>
        <button type="button" className="ghost" onClick={copy} disabled={loading || text === ''}>
          {copied ? t('review.brief.copied') : t('review.brief.copy')}
        </button>
      </div>
      <textarea
        className="monthly-review__brief-text"
        readOnly
        rows={Math.max(4, text.split('\n').length)}
        value={text}
        aria-label={t('review.brief.head')}
      />
    </div>
  );
}
